import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  Typography,
  Box,
  IconButton,
  Divider,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ShareIcon from "@mui/icons-material/Share";
import type { Notification, FriendRequestNotification, ShareRequestNotification } from "../../types";

interface NotificationsPanelProps {
  open: boolean;
  onClose: () => void;
  notifications: Notification[];
  onApprove: (notification: Notification) => void;
  onReject: (notification: Notification) => void;
  onDismiss: (notificationId: string) => void;
}

/**
 * Panel/Dialog for displaying and managing notifications
 */
export const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  open,
  onClose,
  notifications,
  onApprove,
  onReject,
}) => {
  const isFriendRequest = (n: Notification): n is FriendRequestNotification =>
    n.type === "friend-request";

  const isShareRequest = (n: Notification): n is ShareRequestNotification =>
    n.type === "share-request";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Notifications
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary">No notifications yet</Typography>
          </Box>
        ) : (
          <List sx={{ width: "100%" }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                {isFriendRequest(notification) && (
                  <ListItem
                    sx={{
                      bgcolor: notification.read ? "transparent" : "action.hover",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 1,
                      py: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", width: "100%", alignItems: "center", gap: 1 }}>
                      <PersonAddIcon color="secondary" />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2">Friend Request</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {notification.fromDisplayName || notification.fromEmail}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(notification.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => onApprove(notification)}
                        sx={{ flex: 1 }}
                      >
                        Accept
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={() => onReject(notification)}
                        sx={{ flex: 1 }}
                      >
                        Reject
                      </Button>
                    </Stack>
                  </ListItem>
                )}

                {isShareRequest(notification) && (
                  <ListItem
                    sx={{
                      bgcolor: notification.read ? "transparent" : "action.hover",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 1,
                      py: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", width: "100%", alignItems: "center", gap: 1 }}>
                      <ShareIcon color="info" />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2">Share Request</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {notification.fromDisplayName || notification.fromEmail} wants to share an item
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(notification.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ width: "100%", fontStyle: "italic" }}>
                      "{notification.itemTitle}"
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => onApprove(notification)}
                        sx={{ flex: 1 }}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={() => onReject(notification)}
                        sx={{ flex: 1 }}
                      >
                        Deny
                      </Button>
                    </Stack>
                  </ListItem>
                )}

                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
